import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  Badge,
  Button,
  Card,
  CardContent,
  Textarea
} from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';
import { Link } from 'react-router-dom';

import { NotPermitted } from '@/design';
import { ApiError, ProblemType } from '@/domains/api';
import { useCan } from '@/domains/auth';
import { formatDateTime } from '@/domains/survey';
import { oversightApi } from '../api/oversight-api';
import type { IStaffNote } from '../api/oversight-contracts';
import { NOTE_MAX_LENGTH } from '../api/oversight-contracts';
import { responsePath } from '../paths';
import { nameOfEmail, useNoteInitials } from './notes-log';
import { refusalMessage } from '../refusal';

/**
 * Staff notes as both screens that hold them draw them — a response's notes pane and the
 * person hub's Notes section (SR-215): the composer and the list, one rendering each, so a note
 * reads the same wherever it was written and wherever it is read. A note is signed with its
 * author's address, `authorEmail`, which survey-api sends on every note — never the account id
 * the row was stored under, which says nothing to a reader.
 *
 * `staff_note` hangs off the participant, and a note carries `responseId` when it was written
 * from a response — so what a note says about **where it was written** depends on where it is
 * read. On a response, a note on that response needs no mark, and one on another response or
 * on none says so; on the hub there is no current response, so a note on one says it *is* on
 * a response and links to it, and a note on none says that. The list takes the response it is
 * read on, or nothing, and decides the mark from that alone. An author the note initials table
 * knows by address is signed by name, the address on hover (SR-494).
 *
 * **On the hub a note is also marked with whom it is on**, where the household has more than
 * one member: the hub reads every member's notes in one list. The caller hands the names, by
 * participant id, only then — one member's notes need no mark any more than one member's
 * responses need a participant column.
 *
 * **Only a note's author edits or deletes it** (SR-222) — survey-api's own test, the note's
 * `authorAccountId` against the session's `accountId`, never the address; an admin gets no
 * exception, and anybody else is refused `not-note-author`. So Edit and Delete are drawn on the
 * viewer's own notes alone, and the list calls the two routes itself: unlike the composer's
 * write, which goes to a scope only the caller knows, a note's edit and delete are addressed by
 * the note. Each says its refusal beside the note and re-reads on a success — saying so if that
 * re-read fails, since the write still landed; a note found gone (404), and an answer that never
 * arrived (which may have landed), re-read and are said above the list once the note is gone. A
 * note edited is marked "edited" with the time `updatedAt` moved to (the owner's ruling, F4's
 * Q2): nothing else on the wire says so, and no route reads the old body, so none is offered.
 *
 * **Every note write is `responses.annotate`** (SR-228) — the composer's, an edit and a delete.
 * Without it the composer's box and button are drawn off, and the viewer's own notes keep their
 * Edit and Delete drawn off, each under one `NotPermitted` sentence: authorship still decides
 * whose notes carry the buttons, authority whether they press.
 *
 * The composer refuses a blank note before the round trip and caps the length at the server's
 * own ceiling; anything else the server refuses is said in the caller's error slot, which is
 * why the write is the caller's and the refusal comes back to it.
 */

export function NoteComposer({
  placeholder,
  write,
  onRefused,
  beside,
  autoFocus = false,
  onCancel
}: {
  placeholder: string;
  /** Writes the note and re-reads the list — every write re-reads. A refusal is thrown. */
  write: (body: string) => Promise<void>;
  /** Where a refusal is said: the caller's own error slot, beside its load error. */
  onRefused: (message: string) => void;
  /** Drawn beside the button — the hub's choice of member to write on, in a household. */
  beside?: React.ReactNode;
  /** The box takes the cursor as it is drawn — a composer opened by a press (SR-407). */
  autoFocus?: boolean;
  /** Drawn as "Cancel" before the button where given — closing a composer opened by a press. */
  onCancel?: () => void;
}) {
  const [body, setBody] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const permitted = useCan()('responses.annotate');
  const reasonId = React.useId();

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    try {
      await write(body.trim());
      setBody('');
    } catch (writeError) {
      // The typing stays in the box: a refused note is not a lost one.
      onRefused(refusalMessage(writeError, 'Could not write the note'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="grid gap-3" onSubmit={submit}>
      <Textarea
        value={body}
        onChange={(event) => setBody(event.target.value)}
        maxLength={NOTE_MAX_LENGTH}
        rows={3}
        aria-label="Note"
        autoFocus={autoFocus}
        placeholder={placeholder}
        disabled={!permitted}
        aria-describedby={permitted ? undefined : reasonId}
      />
      {!permitted && <NotPermitted id={reasonId} what="writing notes" />}
      <span className="flex flex-wrap items-center justify-end gap-3">
        {beside}
        {onCancel && (
          <Button type="button" variant="ghost" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
        )}
        <Button
          type="submit"
          disabled={!permitted || busy || body.trim() === ''}
          aria-describedby={permitted ? undefined : reasonId}
        >
          Add note
        </Button>
      </span>
    </form>
  );
}

/** What an edit or a delete was told, held by the list so it outlives the note it is about. */
interface INoteSaid {
  noteId: string;
  text: string;
  /** Said above the list once the note is gone from it — a 404's sentence, or an answer that never
   *  arrived, whose re-read may take the note away. Any other refusal is about a note on screen,
   *  and goes with it (another page, another scope). */
  outlives: boolean;
}

export function NoteList({
  notes,
  responseId,
  members,
  viewer,
  onChanged
}: {
  notes: IStaffNote[];
  /** The response the list is read on — absent on the hub. */
  responseId?: string;
  /** Each member's name by participant id — handed only by a hub whose household has more than
   *  one, and then every note says whom it is on. */
  members?: ReadonlyMap<string, string>;
  /** The session's `accountId`: the notes it wrote are the ones it may edit and delete. */
  viewer: string;
  /** Re-reads the list — after every edit or delete that landed, after a 404 and after an answer
   *  that never arrived; resolves `false` when the read failed. */
  onChanged: () => Promise<boolean>;
}) {
  const [said, setSaid] = React.useState<Nullable<INoteSaid>>(null);
  const permitted = useCan()('responses.annotate');
  const table = useNoteInitials();
  const reasonId = React.useId();
  // A sentence about a note the list no longer carries — deleted elsewhere — is said above it.
  const orphaned =
    said !== null && said.outlives && !notes.some((note) => note.noteId === said.noteId);
  const ownNotes = notes.some((note) => note.authorAccountId === viewer);

  return (
    <>
      {!permitted && ownNotes && (
        <NotPermitted id={reasonId} what="editing or deleting your notes" />
      )}
      {orphaned && (
        <p role="alert" className="text-sm text-destructive">
          {said.text}
        </p>
      )}
      {notes.length === 0 ? (
        <p className="text-sm text-muted-foreground">No note yet.</p>
      ) : (
        <ul className="grid gap-3">
          {notes.map((note) => (
            <li key={note.noteId}>
              <Card>
                <CardContent className="grid gap-1 py-4">
                  <span className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                    {formatDateTime(note.createdAt)}
                    {note.updatedAt !== note.createdAt && (
                      <span>edited {formatDateTime(note.updatedAt)}</span>
                    )}
                    <span className="break-all" title={note.authorEmail}>
                      by {nameOfEmail(table, note.authorEmail) ?? note.authorEmail}
                    </span>
                    {members && (
                      <Badge variant="outline">
                        on {members.get(note.participantId) ?? note.participantId}
                      </Badge>
                    )}
                    <WrittenOn note={note} responseId={responseId} />
                  </span>
                  {note.authorAccountId === viewer ? (
                    <OwnNote
                      note={note}
                      said={said?.noteId === note.noteId ? said.text : null}
                      say={(text, outlives = false) =>
                        setSaid(text === null ? null : { noteId: note.noteId, text, outlives })
                      }
                      onChanged={onChanged}
                      notPermitted={permitted ? undefined : reasonId}
                    />
                  ) : (
                    <p className="text-sm whitespace-pre-wrap">{note.body}</p>
                  )}
                </CardContent>
              </Card>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

/**
 * An edit's or a delete's refusal in words, by `type` and status — never by `title`. A 404
 * re-reads, so its sentence is the one the list says above itself.
 */
function noteRefusal(error: unknown, fallback: string): string {
  if (error instanceof ApiError) {
    if (error.problem?.type === ProblemType.notNoteAuthor) {
      return 'Only the note’s author can edit or delete it.';
    }
    if (error.status === 404) return 'That note is no longer there — it was deleted elsewhere.';
  }
  return refusalMessage(error, fallback);
}

/**
 * The viewer's own note: its body, and Edit and Delete. Edit opens the body in place — blank is
 * refused before the press as the composer refuses it, and an unchanged body sends nothing.
 * Delete is a confirmed press, the dialog held open until the answer is in.
 */
function OwnNote({
  note,
  said,
  say,
  onChanged,
  notPermitted
}: {
  note: IStaffNote;
  said: Nullable<string>;
  say: (text: Nullable<string>, outlives?: boolean) => void;
  onChanged: () => Promise<boolean>;
  /** The id of the list's `NotPermitted` sentence; present, Edit and Delete are drawn off. */
  notPermitted?: string;
}) {
  const [draft, setDraft] = React.useState<Nullable<string>>(null);
  const [busy, setBusy] = React.useState(false);
  const [confirming, setConfirming] = React.useState(false);

  /**
   * One write, said or re-read after: a success re-reads, and a re-read that fails after it is said
   * (`landed`) — the write is neither undone nor refused, the list is only as last read. A refusal
   * is said; a 404 re-reads; and an answer that never arrived — no `ApiError`, or a 5xx — is not a
   * refusal: the write may have landed, so the list is read again and the person asked to check
   * (the hub's `unanswered` rule). Resolves whether the write landed.
   */
  const run = async (
    write: () => Promise<unknown>,
    fallback: string,
    landed: string
  ): Promise<boolean> => {
    setBusy(true);
    say(null);
    try {
      try {
        await write();
      } catch (writeError) {
        const unanswered = !(writeError instanceof ApiError) || writeError.status >= 500;
        const gone = writeError instanceof ApiError && writeError.status === 404;
        const rereads = unanswered || gone;
        say(
          unanswered
            ? 'The answer did not arrive — check whether the note changed before saving again.'
            : noteRefusal(writeError, fallback),
          rereads
        );
        if (rereads) await onChanged();
        return false;
      }
      if (!(await onChanged())) say(landed);
      return true;
    } finally {
      setBusy(false);
    }
  };

  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    if (draft === null) return;
    const body = draft.trim();
    if (body === note.body) {
      setDraft(null);
      return;
    }
    const landed = await run(
      () => oversightApi.editNote(note.noteId, body),
      'Could not save the note',
      'Saved — but the notes could not be read again. Reload to see them as stored.'
    );
    if (landed) setDraft(null);
  };

  const remove = async () => {
    await run(
      () => oversightApi.deleteNote(note.noteId),
      'Could not delete the note',
      'Deleted — but the notes could not be read again. Reload to see them as stored.'
    );
    setConfirming(false);
  };

  return (
    <>
      {draft === null ? (
        <p className="text-sm whitespace-pre-wrap">{note.body}</p>
      ) : (
        <form className="grid gap-2" onSubmit={save}>
          <Textarea
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            maxLength={NOTE_MAX_LENGTH}
            rows={3}
            aria-label="Edit note"
            autoFocus
          />
          <span className="flex flex-wrap justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={busy}
              onClick={() => {
                setDraft(null);
                say(null);
              }}
            >
              Cancel
            </Button>
            <Button type="submit" size="sm" disabled={busy || draft.trim() === ''}>
              {busy ? 'Saving…' : 'Save'}
            </Button>
          </span>
        </form>
      )}
      {said && (
        <p role="alert" className="text-sm text-destructive">
          {said}
        </p>
      )}
      {draft === null && (
        <span className="flex flex-wrap justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={busy || notPermitted !== undefined}
            aria-describedby={notPermitted}
            onClick={() => {
              setDraft(note.body);
              say(null);
            }}
          >
            Edit
          </Button>
          <AlertDialog open={confirming} onOpenChange={(open) => !busy && setConfirming(open)}>
            <AlertDialogTrigger asChild>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={busy || notPermitted !== undefined}
                aria-describedby={notPermitted}
              >
                Delete
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete this note?</AlertDialogTitle>
                <AlertDialogDescription>It is gone from every list.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  disabled={busy}
                  onClick={(event) => {
                    // Held open until the answer is in, as the hub's confirmed presses are.
                    event.preventDefault();
                    void remove();
                  }}
                >
                  {busy ? 'Deleting…' : 'Delete it'}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </span>
      )}
    </>
  );
}

/** Where a note was written, said relative to where it is read — see this file's header. */
function WrittenOn({ note, responseId }: { note: IStaffNote; responseId?: string }) {
  if (note.responseId === null) return <Badge variant="outline">no response</Badge>;
  if (responseId === undefined) {
    return (
      <Link
        to={responsePath(note.responseId)}
        className="text-[var(--color-sapphire)] underline-offset-4 hover:underline"
      >
        on a response
      </Link>
    );
  }
  if (note.responseId === responseId) return null;
  return <Badge variant="outline">another response</Badge>;
}
